import { logger } from '@adornis/base/logging.js';
import { LocalStorageReactive } from '@adornis/base/rxjs-extension/index.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeAllEntities, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/popover/x-dropdown-selection.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { combineLatest, map, switchMap } from 'rxjs';

export const timeframeSearchParam = 'timeframe';
const localStorageSelectedTimeframe = new LocalStorageReactive('_locally_selected_timeframe', '');

export const currentlySelectedTimeframe = combineLatest([
  localStorageSelectedTimeframe.asObservable(),
  currentUser(),
]).pipe(
  switchMap(([timeframe, curUser]) => {
    // wenn eingeloggt, dann kann es nur ein Admin sein...
    if (curUser && timeframe)
      // Hole Timeframe von ID aus demLocalStorage
      return subscribeByID(Timeframe, timeframe)({ name: 1, isActive: 1, _id: 1 });

    // Ansonsten ist es ein Anonymer User, der den aktiven Timeframes bekommt
    logger.debug({ timeframe, curUser }, 'No user logged in or selected Timeframe... Fallback to active Timeframe');
    return currentlyActiveTimeframe;
  }),
);

export const currentlyActiveTimeframe = subscribeAllEntities(Timeframe)({ name: 1, isActive: 1, _id: 1 }).pipe(
  map(timeframes => timeframes.find(timeframe => timeframe.isActive)),
);

/*
 * @element matching-semester-selection
 */
@customElement('matching-semester-selection')
export class MachtingSemesterSelection extends ChemistryLitElement {
  @state() private readonly _timeframes = new RXController(
    this,
    subscribeAllEntities(Timeframe)({ name: 1, isActive: 1, _id: 1 }),
  );
  @state() private readonly _activelySelectedTimeframe = new RXController(this, currentlySelectedTimeframe);
  @state() private readonly _activeTimeframe = new RXController(this, currentlyActiveTimeframe);

  override render() {
    return html`
      <x-dropdown-selection
        .selectables=${this._timeframes.value ?? []}
        .renderString=${(timeframe?: Timeframe) => timeframe?.name}
        .value=${this._activelySelectedTimeframe.value}
        placeholder="Daten dieses Semesters werden angezeigt"
        ?clearable=${this._activeTimeframe.value?._id !== this._activelySelectedTimeframe.value?._id}
        @value-picked=${(e: ValueEvent<Maybe<Timeframe>>) => {
          const newTimeframe = e.detail.value;
          if (newTimeframe) localStorageSelectedTimeframe.next(newTimeframe._id);
          else localStorageSelectedTimeframe.next('');
        }}
      ></x-dropdown-selection>
    `;
  }

  override styles() {
    return [...super.styles(), { ':host': { width: '200px', display: 'block' } }];
  }
}
